import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { NextSeo } from "next-seo";

const PrivacyPolicy = () => {
  return (
    <>
      <NextSeo
        title="Privacy Policy | Data Security & Transparency | PlanetHive AI"
        description="PlanetHive AI protects your privacy with robust data safeguards and transparent policies. We prioritize security in every AI-driven solution we deliver."
        canonical="https://www.planethive.ai/privacy-policy"
        openGraph={{
          url: "https://www.planethive.ai/privacy-policy",
          title: "Privacy Matters at Planet Hive AI - Discover Our Comprehensive Data Protection Policy",
          description:
            "Learn how PlanetHive AI ensures data security with transparent policies and advanced privacy measures. Built to safeguard your sensitive information with trust and clarity.",
          images: [
            {
              url: "/images/planet-hive-logo.png",
              width: 421,
              height: 120,
              alt: "Privacy Policy | Planet Hive AI",
            },
          ],
          site_name: "Planet Hive AI",
        }}
        twitter={{
          handle: "@Muneb91",
          site: "@Muneb91",
          cardType: "summary_large_image",
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Privacy Policy",
            description:
              "Learn how Planet Hive AI safeguards your data and ensures privacy while delivering cutting-edge AI solutions.",
            url: "https://www.planethive.ai/privacy-policy",
            publisher: {
              "@type": "Organization",
              name: "Planet Hive AI",
              logo: {
                "@type": "ImageObject",
                url: "/images/planet-hive-logo.png",
                width: 421,
                height: 120,
              },
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://www.planethive.ai",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Privacy Policy",
                  item: "https://www.planethive.ai/privacy-policy",
                },
              ],
            },
          }),
        }}
      />
      <main className="flex flex-col bg-primary min-h-screen">
        <Header />
        <section className="container mx-auto px-4 py-16 mt-40 lg:mt-56">
          <h1
            className="font-orbitron text-4xl font-extrabold text-white mb-8"
            aria-label="Privacy Policy"
          >
            Privacy Policy
          </h1>
          <p className="font-inter text-xl text-white mb-4">
            Last Updated: <time dateTime="2024-06-17">June 17, 2024</time>
          </p>
          <p className="font-inter text-lg text-white mb-4">
            Planet Hive AI ("we," "us," "our") is committed to protecting your
            privacy. This Privacy Policy explains how we collect, use, disclose,
            and safeguard your information when you visit our website, use our
            services, or interact with us in any way.
          </p>

          {/* 1. Information We Collect */}
          <h2
            className="font-orbitron text-2xl font-extrabold text-white mb-4"
            id="information-we-collect"
          >
            1. Information We Collect
          </h2>
          <p className="font-inter text-lg text-white mb-4">
            We collect personal information only when you voluntarily provide it
            to us, such as:
          </p>
          <ul className="list-disc list-inside font-inter text-lg text-white mb-4">
            <li>
              <strong>Contact Form:</strong> Your name, email address, and
              message.
            </li>
            <li>
              <strong>Newsletter Signup:</strong> Your email address.
            </li>
          </ul>

          {/* 2. Use of Your Information */}
          <h3
            className="font-orbitron text-2xl font-extrabold text-white mb-4"
            id="use-of-information"
          >
            2. Use of Your Information
          </h3>
          <p className="font-inter text-lg text-white mb-4">
            Your information is used for:
          </p>
          <ul className="list-disc list-inside font-inter text-lg text-white mb-4">
            <li>Responding to your inquiries and providing support.</li>
            <li>Sending newsletters to subscribed users.</li>
            <li>
              Analyzing website usage with tools like Google Analytics and
              Microsoft Clarity.
            </li>
          </ul>

          {/* 3. Third-Party Services */}
          <h3
            className="font-orbitron text-2xl font-extrabold text-white mb-4"
            id="third-party-services"
          >
            3. Third-Party Services
          </h3>
          <p className="font-inter text-lg text-white mb-4">
            We use trusted third-party services:
          </p>
          <ul className="list-disc list-inside font-inter text-lg text-white mb-4">
            <li>
              <strong>Google Analytics:</strong> Tracks website traffic.
            </li>
            <li>
              <strong>Microsoft Clarity:</strong> Offers session recordings and
              heatmaps for understanding user behavior.
            </li>
          </ul>

          {/* 4. Data Storage and Security */}
          <h3
            className="font-orbitron text-2xl font-extrabold text-white mb-4"
            id="data-storage-security"
          >
            4. Data Storage and Security
          </h3>
          <p className="font-inter text-lg text-white mb-4">
            We take data security seriously and implement industry-standard
            practices to protect your personal information.
          </p>

          {/* 5. Data Sharing */}
          <h3
            className="font-orbitron text-2xl font-extrabold text-white mb-4"
            id="data-sharing"
          >
            5. Data Sharing
          </h3>
          <p className="font-inter text-lg text-white mb-4">
            We do not sell or share your personal information with third
            parties, except with trusted third-party services mentioned above.
          </p>

          {/* 6. User Rights */}
          <h3
            className="font-orbitron text-2xl font-extrabold text-white mb-4"
            id="user-rights"
          >
            6. User Rights
          </h3>
          <p className="font-inter text-lg text-white mb-4">
            You have the right to:
          </p>
          <ul className="list-disc list-inside font-inter text-lg text-white mb-4">
            <li>Request access to your personal data.</li>
            <li>Request correction of inaccurate information.</li>
            <li>Request deletion of your personal data.</li>
          </ul>

          {/* 7. Children’s Privacy */}
          <h3
            className="font-orbitron text-2xl font-extrabold text-white mb-4"
            id="childrens-privacy"
          >
            7. Children’s Privacy
          </h3>
          <p className="font-inter text-lg text-white mb-4">
            We do not knowingly collect information from children under 13. If
            you believe we have inadvertently collected such information, please
            contact us.
          </p>

          {/* 8. Policy Updates */}
          <h3
            className="font-orbitron text-2xl font-extrabold text-white mb-4"
            id="policy-updates"
          >
            8. Policy Updates
          </h3>
          <p className="font-inter text-lg text-white mb-4">
            This Privacy Policy may be updated periodically. Material changes
            will be communicated via email or a notice on our website.
          </p>

          {/* 9. Contact Us */}
          <h3
            className="font-orbitron text-2xl font-extrabold text-white mb-4"
            id="contact-us"
          >
            9. Contact Us
          </h3>
          <p className="font-inter text-lg text-white mb-4">
            Have questions? Reach out via our{" "}
            <a
              href="/contact"
              className="text-[#80ff00] underline hover:text-white"
              aria-label="Contact Us"
            >
              Contact Page
            </a>{" "}
            for support or further assistance.
          </p>
        </section>
        <Footer />
      </main>
    </>
  );
};

export default PrivacyPolicy;
